import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const SELECT = "SELECT";
const PDF_ENDPOINT = "/FillMyForm/BOM_ATM/Print.php";
const STORAGE_KEY = "smart-solutions-bom-atm";
const accountOptions = [[SELECT, SELECT], ["SAVING", "SAVING"], ["CURRENT", "CURRENT"]];

const applicationFields = [
  { name: "branch", label: "Branch Name", maxLength: 20, required: true },
  { name: "actype", label: "Account Type", options: accountOptions, required: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true, account: true, required: true },
  { name: "lname", label: "Last Name", maxLength: 25, required: true, namePart: true, alphaOnly: true },
  { name: "fname", label: "First Name", maxLength: 25, required: true, namePart: true, alphaOnly: true },
  { name: "mname", label: "Middle Name", maxLength: 25, namePart: true, alphaOnly: true },
  { name: "noc", label: "Name on Card", maxLength: 40, required: true, alphaOnly: true },
  { name: "dob", label: "Date of Birth", type: "date", required: true },
  { name: "add1", label: "Address Line 1", maxLength: 35, required: true },
  { name: "add2", label: "Address Line 2", maxLength: 35, required: true },
  { name: "city", label: "City", maxLength: 15, required: true },
  { name: "dist", label: "District", maxLength: 15, required: true },
  { name: "state", label: "State", maxLength: 15, required: true },
  { name: "pin", label: "PIN", maxLength: 6, numeric: true, pin: true, required: true },
  { name: "mobile", label: "Mobile", maxLength: 10, numeric: true, mobile: true, required: true },
];

const initialFormData = Object.fromEntries(applicationFields.map((field) => [field.name, field.options ? SELECT : ""]));
const tabs = [{ title: "Application", fields: applicationFields }, { title: "Preview", fields: [], preview: true }];

function formatDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

function validateField(field, value) {
  const text = String(value ?? "").trim();
  if (field.required && (!text || text === SELECT)) return `${field.label} is required.`;
  if (!text || text === SELECT) return "";
  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(text)) {
    return `${field.label} must contain alphabet letters only.`;
  }
  if (field.account && !/^\d{11}$/.test(text)) return "Enter a valid 11-digit account number.";
  if (field.pin && !/^\d{6}$/.test(text)) return "PIN must contain exactly 6 digits.";
  if (field.mobile && !/^[6-9][0-9]{9}$/.test(text)) return "Enter a valid 10-digit mobile number.";
  return "";
}

export default function ATM() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(() => {
    try { const saved = sessionStorage.getItem(STORAGE_KEY); return saved ? { ...initialFormData, ...JSON.parse(saved) } : initialFormData; }
    catch { return initialFormData; }
  });
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);
  const visibleFields = tabs[currentTab].fields;
  const firstFieldName = visibleFields[0]?.name || "";
  const lastFieldName = visibleFields.at(-1)?.name || "";

  useEffect(() => { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData)); }, [formData]);
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => {
    if (!firstFieldName) return undefined;
    const frameId = requestAnimationFrame(() => document.getElementById(firstFieldName)?.focus());
    return () => cancelAnimationFrame(frameId);
  }, [currentTab, firstFieldName]);

  useEffect(() => {
    const controller = new AbortController();

    const loadAutofill = async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent("BOM_ATM")}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load autofill details.");
        }

        if (!result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,

          branch: previous.branch || result.data.branch || "",

          city: previous.city || result.data.city || "",
          dist: previous.dist || result.data.dist || "",
          state: previous.state || result.data.state || "",
          pin: previous.pin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Autofill error:", error);
        }
      }
    };

    loadAutofill();

    return () => {
      controller.abort();
    };
  }, []);

  const updateValue = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    setFormData((previous) => {
      const next = { ...previous, [field.name]: value };
      if (field.namePart) next.noc = [next.lname, next.fname, next.mname].filter(Boolean).join(" ").slice(0, 40);
      return next;
    });
    setErrors((previous) => ({ ...previous, [field.name]: "", ...(field.namePart ? { noc: "" } : {}) }));
    setPdfError("");
  };

  const validateFields = (fields) => {
    const nextErrors = {};
    fields.forEach((field) => { const message = validateField(field, formData[field.name]); if (message) nextErrors[field.name] = message; });
    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateFields(applicationFields)) return;
    setCurrentTab(1); setMaxAllowedTab(1); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!validateFields(applicationFields)) { setPdfError("Please correct the highlighted fields before submitting."); return; }
    setPdfLoading(true); setPdfError("");
    try {
      const body = new FormData();
      Object.entries(formData).forEach(([name, value]) => body.append(name, value === SELECT ? "" : String(value ?? "")));
      const response = await fetch(PDF_ENDPOINT, { method: "POST", body, credentials: "include", cache: "no-store", headers: { Accept: "application/pdf" } });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.toLowerCase().includes("application/pdf")) {
        const text = await response.text();
        throw new Error(text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `PDF generation failed. HTTP status: ${response.status}`);
      }
      const url = URL.createObjectURL(await response.blob());
      setPdfUrl((previous) => { if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous); return url; });
      setShowPdf(true);
    } catch (error) { setPdfError(error.message || "Unable to generate PDF preview."); }
    finally { setPdfLoading(false); }
  };

  const renderField = (field) => (
    <div key={field.name} className="space-y-2">
      <Label htmlFor={field.name}>{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
      {field.options ? (
        <Select value={formData[field.name]} onValueChange={(value) => updateValue(field, value)}>
          <SelectTrigger id={field.name} className={errors[field.name] ? "border-red-500" : ""}><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
          <SelectContent>{field.options.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input id={field.name} name={field.name} type={field.type || "text"} value={formData[field.name]} placeholder={field.label} maxLength={field.maxLength} inputMode={field.numeric ? "numeric" : undefined} onChange={(event) => updateValue(field, event.target.value)} onKeyDown={(event) => { if (field.name === lastFieldName && event.key === "Tab" && !event.shiftKey) { event.preventDefault(); nextButtonRef.current?.focus(); } }} className={errors[field.name] ? "border-red-500" : ""} autoComplete="on" />
      )}
      {errors[field.name] && <p className="text-sm text-red-500">{errors[field.name]}</p>}
    </div>
  );

  if (showPdf && pdfUrl) return <PdfViewerContainer pdfUrl={pdfUrl} title="BOM ATM Application Form" backRoute="/Fill_My_Form" onBack={() => setShowPdf(false)} />;

  const previewRows = applicationFields.map((field) => [field.label, field.name === "dob" ? formatDate(formData.dob) : field.options?.find(([value]) => value === formData[field.name])?.[1] || formData[field.name]]);
  return (
    <div className="min-h-screen bg-background p-6"><div className="max-w-7xl mx-auto">
      <Card className="mb-6"><CardHeader><CardTitle className="text-3xl">🏦 Bank of Maharashtra ATM Application Form</CardTitle><p className="text-muted-foreground">Fill ATM card application details carefully.</p></CardHeader></Card>
      <div className="grid grid-cols-2 gap-3 mb-8">{tabs.map((tab, index) => <Button key={tab.title} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab || pdfLoading} onClick={() => setCurrentTab(index)}>{index + 1}. {tab.title}</Button>)}</div>
      {currentTab === 0 && <Card><CardHeader><CardTitle>💳 ATM Application Details</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{applicationFields.map(renderField)}</div></CardContent></Card>}
      {currentTab === 1 && <Card><CardHeader><CardTitle>✅ Preview</CardTitle></CardHeader><CardContent><PreviewSection rows={previewRows} /></CardContent></Card>}
      {pdfLoading && <div className="mt-6 text-center">Generating PDF, please wait...</div>}
      {pdfError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{pdfError}</div>}
      <div className="flex justify-between mt-8"><Button type="button" variant="outline" onClick={() => setCurrentTab(0)} disabled={currentTab === 0 || pdfLoading}>← Previous</Button>{currentTab === 1 ? <Button type="button" onClick={handleSubmit} disabled={pdfLoading}>{pdfLoading ? "Generating PDF..." : "Submit"}</Button> : <Button ref={nextButtonRef} type="button" onClick={goNext}>Preview</Button>}</div>
    </div></div>
  );
}

function PreviewSection({ rows }) {
  return <div className="rounded-2xl border p-6"><h3 className="text-xl font-semibold text-center mb-6">Applicant Details</h3><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{rows.map(([label, value]) => <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2"><div className="font-medium text-muted-foreground">{label}</div><div>{value && value !== SELECT ? value : "-"}</div></div>)}</div></div>;
}
